/**
 * Settings Page
 * Application settings and preferences with sidebar navigation
 */

import { ChevronRight } from "lucide-react"
import { useEffect, useState } from "react"
import { useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet"
import { useTranslation } from "@/language"
import { AccountPrivacySection } from "../components/AccountPrivacySection"
import { ChangeEmailSection } from "../components/ChangeEmailSection"
import { ChangePhoneSection } from "../components/ChangePhoneSection"
import { ConnectedAccountsSection } from "../components/ConnectedAccountsSection"
import { HelpCenterSection } from "../components/HelpCenterSection"
import { InviteFriendsSection } from "../components/InviteFriendsSection"
import { LanguageSection } from "../components/LanguageSection"
import { NotificationsSection } from "../components/NotificationsSection"
import { PasswordSecuritySection } from "../components/PasswordSecuritySection"
import { PaymentsEarningsSection } from "../components/PaymentsEarningsSection"
import { PrivacyPolicySection } from "../components/PrivacyPolicySection"
import { RemoveAccountSection } from "../components/RemoveAccountSection"
import { SettingsSidebar } from "../components/SettingsSidebar"
import { SubscriptionSection } from "../components/SubscriptionSection"
import { TimeZoneSection } from "../components/TimeZoneSection"
import type { SettingsSection } from "../types/settings.types"

const SECTION_COMPONENTS: Record<SettingsSection, React.ComponentType> = {
  "password-security": PasswordSecuritySection,
  "change-phone": ChangePhoneSection,
  "change-email": ChangeEmailSection,
  "account-privacy": AccountPrivacySection,
  "connected-accounts": ConnectedAccountsSection,
  subscription: SubscriptionSection,
  "payments-earnings": PaymentsEarningsSection,
  "invite-friends": InviteFriendsSection,
  notifications: NotificationsSection,
  language: LanguageSection,
  timezone: TimeZoneSection,
  "help-center": HelpCenterSection,
  "privacy-policy": PrivacyPolicySection,
  "remove-account": RemoveAccountSection,
}

export function SettingsScreen() {
  const { t } = useTranslation("settings")
  const [searchParams] = useSearchParams()
  const [activeSection, setActiveSection] = useState<SettingsSection>(() =>
    getSettingsSection(searchParams.get("section")),
  )
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== "undefined") {
      return window.innerWidth < 1024 // lg breakpoint
    }
    return false
  })

  // Handle OAuth and checkout callback params
  useEffect(() => {
    const oauthSuccess = searchParams.get("oauth")
    const oauthError = searchParams.get("oauth_error")
    const provider = searchParams.get("provider")
    const requestedSection = getSettingsSection(searchParams.get("section"))
    const checkoutSuccess = searchParams.get("success")
    const checkoutCanceled = searchParams.get("canceled")

    setActiveSection(requestedSection)

    // Handle Stripe checkout success - switch to subscription tab
    if (checkoutSuccess === "true") {
      toast.success(t("subscription.checkoutSuccess"))
      setActiveSection("subscription")
      // Clear URL params
      window.history.replaceState({}, "", buildSettingsUrl("subscription"))
    }

    // Handle Stripe checkout canceled - switch to subscription tab
    if (checkoutCanceled === "true") {
      toast.info(t("subscription.checkoutCanceled"))
      setActiveSection("subscription")
      // Clear URL params
      window.history.replaceState({}, "", buildSettingsUrl("subscription"))
    }

    if (oauthSuccess === "success") {
      toast.success(t("connectedAccounts.connectedSuccess", { provider }))
      // Clear URL params
      window.history.replaceState({}, "", buildSettingsUrl(requestedSection))
    }

    if (oauthError) {
      toast.error(`OAuth error: ${oauthError}`)
      // Clear URL params
      window.history.replaceState({}, "", buildSettingsUrl(requestedSection))
    }
  }, [searchParams, t])

  const ActiveComponent = SECTION_COMPONENTS[activeSection]

  return (
    <div className="bg-background min-h-screen py-0 relative px-5 md:px-17.5">
      <div className="max-w-360 mx-auto m-5 mt-[50px] border-2 border-card rounded-lg">
        {/* Layout: Sidebar + Content */}
        <div className="flex">
          {/* Mobile: Hamburger Menu + Sheet Drawer */}
          <div className="lg:hidden">
            <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
              <SheetTrigger asChild>
                <button
                  type="button"
                  className="p-2 my-5 mx-2 rounded-lg hover:bg-beige-soft transition-colors absolute top-0 left-0 cursor-pointer"
                  aria-label="Open settings menu"
                >
                  <ChevronRight className="size-5" />
                </button>
              </SheetTrigger>
              <SheetContent side="left" className="w-90 p-0">
                <aside className="w-full h-full bg-card px-1 py-5 overflow-y-auto">
                  <SettingsSidebar
                    activeSection={activeSection}
                    onSectionChange={setActiveSection}
                    onClose={() => setSidebarOpen(false)}
                  />
                </aside>
              </SheetContent>
            </Sheet>
          </div>

          {/* Desktop: Sidebar */}
          <aside className="hidden lg:block w-94 shrink-0 bg-card py-5 max-h-[calc(100vh-2.5rem)] overflow-y-auto rounded-tl-lg rounded-bl-lg">
            <SettingsSidebar activeSection={activeSection} onSectionChange={setActiveSection} />
          </aside>

          {/* Content */}
          <main className="flex-1 min-w-0 p-7.5 bg-[#FFFFFF0D] rounded-tr-lg rounded-br-lg h-[calc(100vh-2.5rem)] overflow-y-scroll">
            <ActiveComponent />
          </main>
        </div>
      </div>
    </div>
  )
}

function getSettingsSection(sectionParam: string | null): SettingsSection {
  const validSections: SettingsSection[] = [
    "password-security",
    "change-phone",
    "change-email",
    "account-privacy",
    "connected-accounts",
    "subscription",
    "payments-earnings",
    "invite-friends",
    "notifications",
    "language",
    "timezone",
    "help-center",
    "privacy-policy",
    "remove-account",
  ]

  return validSections.includes(sectionParam as SettingsSection)
    ? (sectionParam as SettingsSection)
    : "change-phone"
}

function buildSettingsUrl(section: SettingsSection): string {
  return section === "change-phone" ? "/settings" : `/settings?section=${section}`
}
